import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import ConfirmModal from '../components/ConfirmModal';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import { FaPlus, FaChevronDown, FaTimes, FaDollarSign, FaArrowUp, FaArrowDown, FaSlidersH, FaTrash, FaEdit, FaBuilding, FaCalendarAlt, FaTag, FaUser, FaStickyNote, FaBolt, FaCheckCircle, FaUniversity } from 'react-icons/fa';
import './Sidebar.css';
import './Navbar.css';
import './Activity.css';
import Tutorial from './Tutorial';
import { showToast } from '../components/toast';

const BASE_URL = 'https://api.rside.app';

function Activity() {
  const navigate = useNavigate();
  const location = useLocation();
  const [initials, setInitials] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const askConfirm = (opts, onConfirm) => setConfirmDialog({ ...opts, onConfirm });
  const [modalType, setModalType] = useState(null);
  const [properties, setProperties] = useState([]);
  const [activities, setActivities] = useState([]);
  const [demoMode, setDemoMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [filterRent, setFilterRent]             = useState(true);
  const [filterFeeIncome, setFilterFeeIncome]   = useState(true);
  const [filterForgiveness, setFilterForgiveness] = useState(true);
  const [filterPushedFee, setFilterPushedFee]   = useState(true);
  const [filterExpense, setFilterExpense]       = useState(true);
  const [filterYear, setFilterYear] = useState(null);
  const [filterMonthStart, setFilterMonthStart] = useState(1);
  const [filterMonthEnd, setFilterMonthEnd] = useState(12);
  const [filterAmountFrom, setFilterAmountFrom] = useState('');
  const [filterAmountTo, setFilterAmountTo] = useState('');
  const [filterExpenseCategory, setFilterExpenseCategory] = useState('');
  const [showExpenseCats, setShowExpenseCats] = useState(false);
  const [editingIncomeId, setEditingIncomeId] = useState(null);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [currentEditActivity, setCurrentEditActivity] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [expandedSplits, setExpandedSplits] = useState(new Set());
  const [availableYearsFromApi, setAvailableYearsFromApi] = useState([]);
  const [doorsMap, setDoorsMap] = useState({});  // propertyId → [{ doorId, unitNumber }]
  const pmEmailRef = useRef('');
  const propertiesRef = useRef([]);
  const itemsPerPage = 10;

  // Tutorial interaction
  const tutorialFilterOpened = useRef(false);
  const handleTutorialStep = useCallback((stepIndex, stepConfig) => {
    const anchorId = stepConfig?.anchorId;
    if (stepIndex === -1) {
      if (tutorialFilterOpened.current) { setShowFilter(false); tutorialFilterOpened.current = false; }
      if (demoMode) {
        localStorage.removeItem(`isDemoActivity_${userEmail}`);
        setDemoMode(false);
        setActivities(prev => prev.filter(a => !a.id.startsWith('demo-')));
      }
      return;
    }
    if (anchorId === 'activity-filter-drawer') {
      setShowFilter(true); tutorialFilterOpened.current = true;
    } else if (tutorialFilterOpened.current) {
      setShowFilter(false); tutorialFilterOpened.current = false;
    }
  }, [demoMode, userEmail]);

  const [expenseForm, setExpenseForm] = useState({
    propertyId: '',
    doorId: '',
    title: '',
    amount: '',
    payDate: new Date().toISOString().split('T')[0],
    category: '',
    utilityType: '',
    notes: ''
  });

  const [incomeForm, setIncomeForm] = useState({
    propertyId: '',
    doorId: '',
    category: 'Rent',
    month: '',
    paymentAmount: '',
    date: new Date().toISOString().split('T')[0],
    method: '',
    notes: '',
    partial: false,
    forgivenessMonth: '',
    forgiveness_id: null,
    tenantId: null,
    selectedFeeItems: [],
  });
  const [expectedRent, setExpectedRent] = useState(null);

  const [feeForm, setFeeForm] = useState({ propertyId: '', unitNumber: '', doorId: null, tenantId: null, feeId: '', notes: '', selectedFee: null });
  const [availableFees, setAvailableFees] = useState([]);
  const [feeFormLoading, setFeeFormLoading] = useState(false);
  const [incomeFeeOptions, setIncomeFeeOptions] = useState([]);
  const [incomeFeeLoading, setIncomeFeeLoading] = useState(false);
  const [forgivenessResolveError, setForgivenessResolveError] = useState(false);
  const [unitIsVacant, setUnitIsVacant] = useState(false);       // true = no active tenant on selected door
  const [unitOccupancyChecked, setUnitOccupancyChecked] = useState(false); // true once API resolved

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return navigate('/login');
      if (localStorage.getItem(`isDemoActivity_${user.email}`) === 'true') {
        localStorage.removeItem(`hasSeenActivityTutorial_${user.email}`);
      }
      setUserEmail(user.email);
      try {
        const res = await fetch(`${BASE_URL}/PropertyManagerName?email=${user.email}`);
        const data = await res.json();
        const pmEmail = data.Email || user.email;
        pmEmailRef.current = pmEmail;
        const parts = data.Full_Name?.split(' ') || [];
        setInitials((parts[0]?.[0] || '') + (parts.at(-1)?.[0] || ''));

        const propRes = await fetch(
          `${BASE_URL}/GetAllProperties?full_name=${encodeURIComponent(data.Full_Name)}&Email=${encodeURIComponent(user.email)}`
        );
        if (propRes.ok) {
          const propData = await propRes.json();
          const rawProps = propData.Properties || [];
          const propertiesList = rawProps.map(p => ({
            id: p.Property_ID,
            doorId: p.Door_id,
            name: p.Property_Name,
            address: p.Street_Address,
            unitNumber: p.Unit_Number,
            numUnits: p.Num_Of_Units,
          }));
          const sortedProps = propertiesList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          setProperties(sortedProps);
          propertiesRef.current = sortedProps;

          // For multi-family properties, load all doors via GetPropertyDetails
          const uniqueMulti = rawProps.filter((p, i, arr) =>
            parseInt(p.Num_Of_Units) > 1 && arr.findIndex(x => x.Property_ID === p.Property_ID) === i
          );
          if (uniqueMulti.length > 0) {
            const newDoorsMap = {};
            await Promise.allSettled(uniqueMulti.map(async (p) => {
              try {
                const dr = await fetch(
                  `${BASE_URL}/GetPropertyDetails?property_name=${encodeURIComponent(p.Property_Name)}&email=${encodeURIComponent(user.email)}`
                );
                if (dr.ok) {
                  const dd = await dr.json();
                  newDoorsMap[p.Property_ID] = (dd.results || []).map(d => ({
                    doorId: d.door_id,
                    unitNumber: d.Unit_Number || ''
                  }));
                }
              } catch {}
            }));
            setDoorsMap(newDoorsMap);
          }
        }

        await fetchActivities(pmEmail);
        if (localStorage.getItem(`isDemoActivity_${user.email}`) === 'true') {
          const today = new Date();
          const threeDaysAgo = new Date(today - 86400000 * 3);
          setActivities(prev => prev.length === 0 ? [
            {
              id: 'demo-rent-1', no: 1,
              amount: 2400, type: 'Rent',
              propertyName: 'Sample Property ✦', propertyAddress: '',
              vendor: 'Tenant', rawDate: today.toISOString(),
              date: today.toLocaleDateString(), notes: 'Monthly rent payment',
              monthOf: '', activityType: 'rent', isPartial: false, category: 'Rent',
            },
            {
              id: 'demo-exp-1', no: 2,
              amount: 890, type: 'Expense',
              propertyName: 'Sample Property ✦', propertyAddress: '',
              vendor: 'Sample Vendor', rawDate: threeDaysAgo.toISOString(),
              date: threeDaysAgo.toLocaleDateString(), notes: 'General maintenance',
              monthOf: '', activityType: 'expense', isPartial: false, category: 'Maintenance',
            },
          ] : prev);
          setDemoMode(true);
          localStorage.removeItem(`hasSeenActivityTutorial_${user.email}`);
        }
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, [navigate]);

  useEffect(() => {
    if (!loading && location.state?.openModal) {
      setModalType(location.state.openModal);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [loading, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // When year changes: current year → Jan to this month; past year → full Jan–Dec
  useEffect(() => {
    if (filterYear === null) return;
    const now = new Date();
    const currentYear = now.getFullYear();
    setFilterMonthStart(1);
    setFilterMonthEnd(filterYear === currentYear ? now.getMonth() + 1 : 12);
  }, [filterYear]);

  const fetchActivities = async (pmEmail) => {
    try {
      setLoading(true);
      const yearsSet = new Set();

      const _now = new Date();
      const res = await fetch(`${BASE_URL}/ActivityFeed?pm_email=${encodeURIComponent(pmEmail)}&end_year=${_now.getFullYear()}&end_month=12`);
      if (!res.ok) throw new Error(`ActivityFeed ${res.status}`);
      const data = await res.json();

      if (data.min_year && data.max_year) {
        for (let y = data.min_year; y <= data.max_year; y++) yearsSet.add(y);
      }

      // Parse "September 2026" → "2026-09-01" for the forgiveness month edit input
      const MONTH_MAP = { January:1,February:2,March:3,April:4,May:5,June:6,July:7,August:8,September:9,October:10,November:11,December:12 };
      const parseMonthOf = (s) => {
        if (!s) return '';
        if (/^\d{4}-\d{2}/.test(s)) return s;
        const [name, yr] = s.split(' ');
        const mo = MONTH_MAP[name];
        return (mo && yr) ? `${yr}-${String(mo).padStart(2,'0')}-01` : '';
      };

      const allActivities = (data.items || []).map((item, idx) => {
        const rawDate = item.paydateString || '';
        // timestampString has the full local datetime for rent/expense (used for sort); paydateString is bare date for display
        const tsStr = item.timestampString || rawDate;
        if (rawDate) { const yr = new Date(rawDate.substring(0,10) + 'T00:00:00').getFullYear(); if (!isNaN(yr)) yearsSet.add(yr); }
        const date = rawDate ? new Date(/^\d{4}-\d{2}-\d{2}/.test(rawDate) ? rawDate.substring(0,10) + 'T00:00:00' : rawDate).toLocaleDateString() : '';

        if (item.item_type === 'expense') {
          return {
            id: `expense-${item.expense_id || item.id || idx}`,
            expenseId: item.expense_id || null,
            sortId: parseInt(item.expense_id || item.id || 0, 10),
            amount: -Math.abs(Number(item.Amount || 0)),
            type: item.Category || 'Expense',
            propertyName: item.Property_Name || '',
            propertyAddress: item.Street_Address || '',
            vendor: item.title || '',
            rawDate: tsStr, date,
            notes: item.Notes || '',
            unitNumber: item.Unit_Number || '',
            activityType: 'expense',
          };
        }

        if (item.item_type === 'rent_forgiveness') {
          const monthOfDisplay = item.MonthOf || '';
          const rawMonthOf = parseMonthOf(monthOfDisplay);
          return {
            id: `rent-${item.rent_forgiveness_id || item.id || idx}`,
            rentId: item.rent_forgiveness_id || null,
            sortId: parseInt(item.rent_forgiveness_id || item.id || 0, 10),
            amount: Math.abs(Number(item.Amount || 0)),
            type: 'Rent Forgiveness',
            propertyName: item.Property_Name || '',
            propertyAddress: item.Street_Address || '',
            vendor: item.Full_Name || 'Tenant',
            method: '',
            rawDate: tsStr, date,
            notes: item.Notes || item.Reason || '',
            monthOf: monthOfDisplay,
            rawMonthOf,
            activityType: 'rent_forgiveness',
            isPartial: false,
            category: 'Rent Forgiveness',
            forgiveness_id: item.rent_forgiveness_id || item.id,
            unitNumber: item.Unit_Number || '',
          };
        }

        if (item.item_type === 'rent') {
          const monthOfDisplay = item.MonthOf || '';
          const rawMonthOf = parseMonthOf(monthOfDisplay);
          return {
            id: `rent-${item.rent_id || item.id || idx}`,
            rentId: item.rent_id || null,
            sortId: parseInt(item.rent_id || item.id || 0, 10),
            amount: Math.abs(Number(item.Amount || 0)),
            type: item.Category === 'Other' ? 'Income' : 'Rent',
            propertyName: item.Property_Name || '',
            propertyAddress: item.Street_Address || '',
            vendor: item.Full_Name || item.Method || 'Tenant',
            method: item.Method || '',
            rawDate: tsStr, date,
            notes: item.Notes || '',
            monthOf: monthOfDisplay,
            rawMonthOf,
            activityType: 'rent',
            isPartial: !!(item.Partial_payment),
            category: item.Category || 'Rent',
            forgiveness_id: null,
            unitNumber: item.Unit_Number || '',
          };
        }

        if (item.item_type === 'pushed_fee') {
          return {
            id: `fee-${item.pushed_fee_id || item.id || idx}`,
            sortId: parseInt(item.pushed_fee_id || item.id || 0, 10),
            amount: Math.abs(Number(item.Amount || 0)),
            type: item.Fee_Type || 'Pushed Fee',
            propertyName: item.Property_Name || item.Street_Address || '',
            propertyAddress: item.Street_Address || '',
            vendor: item.Full_Name || 'Tenant',
            rawDate: tsStr, date,
            notes: item.Notes || '',
            activityType: 'pushed_fee',
            category: 'Pushed Fee',
            status: (item.Status || 'pending').toLowerCase(),
            pushedFeeId: item.pushed_fee_id || null,
            unitNumber: item.Unit_Number || '',
            tenantId: item.Tenant_id,
            doorId: item.Door_id,
          };
        }

        return null;
      }).filter(Boolean);

      // Group multi-unit split expenses (same title + date + property + per-unit amount)
      const splitGroupMap = {};
      allActivities.forEach(act => {
        if (act.activityType === 'expense' && act.unitNumber) {
          const key = `${act.vendor}|${(act.rawDate || '').slice(0,10)}|${act.propertyName}|${Math.abs(act.amount).toFixed(2)}|${act.notes}`;
          if (!splitGroupMap[key]) splitGroupMap[key] = [];
          splitGroupMap[key].push(act);
        }
      });
      const splitChildIds = new Set();
      const splitParents = [];
      Object.values(splitGroupMap).forEach(group => {
        if (group.length >= 2) {
          group.forEach(a => splitChildIds.add(a.id));
          const total = group.reduce((s, a) => s + Math.abs(a.amount), 0);
          splitParents.push({
            id: `split-${group.map(a => a.expenseId).join('-')}`,
            activityType: 'splitExpense',
            vendor: group[0].vendor,
            propertyName: group[0].propertyName,
            amount: -total,
            date: group[0].date,
            rawDate: group[0].rawDate,
            notes: group[0].notes,
            type: group[0].type,
            unitCount: group.length,
            perUnit: Math.abs(group[0].amount),
            children: group,
            sortId: Math.max(...group.map(a => a.sortId)),
          });
        }
      });
      const finalActivities = [
        ...splitParents,
        ...allActivities.filter(a => !splitChildIds.has(a.id)),
      ].sort((a, b) => new Date(b.rawDate) - new Date(a.rawDate));

      finalActivities.forEach((a, idx) => { a.no = idx + 1; });
      setActivities(finalActivities);

      const sortedYears = [...yearsSet].sort((a, b) => b - a);
      if (sortedYears.length > 0) {
        setAvailableYearsFromApi(sortedYears);
        setFilterYear(prev => (prev === null ? sortedYears[0] : prev));
      }

    } catch (err) {
      console.error('Error fetching activities:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExpenseChange = (e) => {
    const { name, value } = e.target;
    setExpenseForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'propertyId') {
        const doors = getDoorsForProperty(value);
        updated.doorId = doors.length === 1 ? String(doors[0].doorId) : '';
      }
      if (name === 'category' && value !== 'Operation Expense' && value !== 'Utility') {
        updated.utilityType = '';
      }
      return updated;
    });
  };

  const handleIncomeChange = (e) => {
    const { name, value } = e.target;
    if (name === 'category' && value !== 'Rent') setExpectedRent(null);
    setIncomeForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'propertyId') {
        const doors = getDoorsForProperty(value);
        updated.doorId = doors.length === 1 ? String(doors[0].doorId) : '';
      }
      return updated;
    });
  };

  // Auto-fill rent amount from API when Rent is selected + door is known
  useEffect(() => {
    if (modalType !== 'income' || incomeForm.category !== 'Rent' || editingIncomeId) return;

    let doorId = incomeForm.doorId;
    if (!doorId && incomeForm.propertyId) {
      const doors = getDoorsForProperty(incomeForm.propertyId);
      if (doors.length === 1) doorId = String(doors[0].doorId);
    }
    if (!doorId) { setUnitIsVacant(false); setUnitOccupancyChecked(false); return; }

    // Clear immediately so switching to a vacant unit doesn't show the prior unit's rent
    setExpectedRent(null);
    setIncomeForm(prev => ({ ...prev, paymentAmount: '' }));
    setUnitIsVacant(false);
    setUnitOccupancyChecked(false);

    let cancelled = false;
    fetch(`${BASE_URL}/Lease&TenantsByDoor?door_id=${doorId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (cancelled || !d) return;
        const tenantList = d.tenants || (Array.isArray(d) ? d : []);
        const rent = tenantList[0]?.Rent || d.tenant?.Rent;
        if (rent) {
          setExpectedRent(parseFloat(rent));
          setIncomeForm(prev => ({ ...prev, paymentAmount: String(rent) }));
          setUnitIsVacant(false);
        } else {
          setUnitIsVacant(true);
        }
        setUnitOccupancyChecked(true);
      })
      .catch(() => { if (!cancelled) { setUnitIsVacant(true); setUnitOccupancyChecked(true); } });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomeForm.propertyId, incomeForm.doorId, incomeForm.category, modalType, editingIncomeId]);

  // Fetch tenant ID when Rent Forgiveness is selected and a door is known
  useEffect(() => {
    if (modalType !== 'income' || incomeForm.category !== 'Rent Forgiveness') return;
    let doorId = incomeForm.doorId;
    if (!doorId && incomeForm.propertyId) {
      const doors = getDoorsForProperty(incomeForm.propertyId);
      if (doors.length === 1) doorId = String(doors[0].doorId);
    }
    if (!doorId) { setForgivenessResolveError(false); return; }
    setForgivenessResolveError(false);
    let cancelled = false;
    fetch(`${BASE_URL}/Lease&TenantsByDoor?door_id=${encodeURIComponent(doorId)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (cancelled) return;
        const t = d?.tenants?.[0] || d?.tenant;
        const tid = t?.id || t?.tenant_id || t?.Tenant_ID || null;
        if (tid) { setIncomeForm(prev => ({ ...prev, tenantId: tid })); setForgivenessResolveError(false); }
        else setForgivenessResolveError(true);
      })
      .catch(() => { if (!cancelled) setForgivenessResolveError(true); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomeForm.propertyId, incomeForm.doorId, incomeForm.category, modalType]);

  // Fetch fee options when income category = Fee and a door is known
  useEffect(() => {
    if (modalType !== 'income' || incomeForm.category !== 'Fee') { setIncomeFeeOptions([]); return; }
    let doorId = incomeForm.doorId;
    if (!doorId && incomeForm.propertyId) {
      const doors = getDoorsForProperty(incomeForm.propertyId);
      if (doors.length === 1) doorId = String(doors[0].doorId);
    }
    if (!doorId) { setIncomeFeeOptions([]); return; }
    setIncomeFeeLoading(true);
    const savedMethod = incomeForm.method; // capture before async
    fetch(`${BASE_URL}/GetFeesByDoor?door_id=${doorId}`)
      .then(r => r.ok ? r.json() : { fees: [] })
      .then(d => {
        const options = d.fees || [];
        setIncomeFeeOptions(options);
        // When editing, pre-check the fees that were previously saved (matched by name)
        if (editingIncomeId && savedMethod) {
          const savedNames = savedMethod.split(',').map(s => s.trim().toLowerCase());
          const preSelected = options
            .filter(f => {
              const fname = (f.Type || f.type || f.Fee_Type || f.fee_name || f.name || '').toLowerCase();
              return savedNames.includes(fname);
            })
            .map(f => ({
              id: String(f.id || f.Fee_Id || ''),
              name: f.Type || f.type || f.Fee_Type || f.fee_name || f.name || 'Fee',
              amount: parseFloat(f.Amount || f.amount || 0),
            }));
          if (preSelected.length > 0) {
            const total = preSelected.reduce((s, f) => s + f.amount, 0);
            setIncomeForm(prev => ({ ...prev, selectedFeeItems: preSelected, paymentAmount: String(total.toFixed(2)) }));
          }
        }
      })
      .catch(() => setIncomeFeeOptions([]))
      .finally(() => setIncomeFeeLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomeForm.propertyId, incomeForm.doorId, incomeForm.category, modalType]);

  const handleSaveExpense = async () => {
    try {
      const doors = getDoorsForProperty(expenseForm.propertyId);
      const isMultiFamily = doors.length > 1;
      const selectedDoorId = expenseForm.doorId;

      if (!expenseForm.propertyId || !expenseForm.title || !expenseForm.amount || !expenseForm.payDate || !expenseForm.category) {
        showToast('Please fill in all required fields', 'warning');
        return;
      }

      if ((expenseForm.category === 'Operation Expense' || expenseForm.category === 'Utility') && !expenseForm.utilityType) {
        showToast('Please select a Type', 'warning');
        return;
      }

      if (expenseForm.category === 'Other' && !expenseForm.notes.trim()) {
        showToast('Notes are required when category is Other', 'warning');
        return;
      }

      if (!selectedDoorId && !isMultiFamily) {
        showToast('Please select a property', 'warning');
        return;
      }

      // Resolve final API category: sub-types map to their own category string
      const resolvedCategory =
        expenseForm.category === 'Operation Expense'
          ? (expenseForm.utilityType === 'General' ? 'Operation Expense' : expenseForm.utilityType)
          : expenseForm.category === 'Utility'
          ? (expenseForm.utilityType === 'General' ? 'Utility' : expenseForm.utilityType)
          : expenseForm.category;

      // Single door (or user selected a specific door)
      if (selectedDoorId || doors.length === 1) {
        const doorId = selectedDoorId || doors[0].doorId;

        const params = new URLSearchParams({
          title: expenseForm.title,
          notes: expenseForm.notes || '',
          amount: parseFloat(expenseForm.amount),
          paydate: expenseForm.payDate + 'T' + new Date().toTimeString().slice(0, 8),
          category: resolvedCategory,
          property_id: parseInt(expenseForm.propertyId, 10),
          door_id: parseInt(doorId, 10),
          source: 'App'
        });

        let url = `${BASE_URL}/CreateExpense?${params.toString()}`;
        let method = 'POST';

        if (editingExpenseId) {
          params.append('expense_id', editingExpenseId);
          url = `${BASE_URL}/EditExpense?${params.toString()}`;
          method = 'PUT';
        }

        const res = await fetch(url, { method, headers: { accept: 'application/json' } });
        if (res.ok) {
          showToast(editingExpenseId ? 'Expense updated successfully!' : 'Expense added successfully!', 'success');
          closeModal();
          await fetchActivities(pmEmailRef.current || userEmail);
          if (!editingExpenseId) window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          const errorText = await res.text();
          console.error('Expense API failed:', errorText);
          showToast(editingExpenseId ? 'Failed to update expense.' : 'Failed to add expense.', 'error');
        }
        return;
      }

      // Multi-family, no door selected — split equally across all doors
      const splitAmount = (parseFloat(expenseForm.amount) / doors.length).toFixed(2);
      const results = await Promise.allSettled(
        doors.map(door => {
          const params = new URLSearchParams({
            title: expenseForm.title,
            notes: expenseForm.notes || '',
            amount: parseFloat(splitAmount),
            paydate: expenseForm.payDate + 'T' + new Date().toTimeString().slice(0, 8),
            category: resolvedCategory,
            property_id: parseInt(expenseForm.propertyId, 10),
            door_id: parseInt(door.doorId, 10),
            source: 'App'
          });
          return fetch(`${BASE_URL}/CreateExpense?${params.toString()}`, {
            method: 'POST',
            headers: { accept: 'application/json' }
          });
        })
      );

      const failed = results.filter(r => r.status === 'rejected' || !r.value?.ok).length;
      if (failed > 0) {
        showToast(`Expense split: ${doors.length - failed} of ${doors.length} units saved successfully.`, 'success');
      } else {
        showToast(`Expense split equally across ${doors.length} units ($${splitAmount} each).`, 'info');
      }
      closeModal();
      await fetchActivities(pmEmailRef.current || userEmail);
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (err) {
      console.error('Error saving expense:', err);
      showToast('Error saving expense.', 'error');
    }
  };

  const handleSaveIncome = async () => {
    try {
      const doors = getDoorsForProperty(incomeForm.propertyId);
      const isMultiFamily = doors.length > 1;
      const selectedDoorId = incomeForm.doorId;

      // ── Rent Forgiveness flow ───────────────────────────────────────────
      if (incomeForm.category === 'Rent Forgiveness') {
        if (!incomeForm.paymentAmount || !incomeForm.forgivenessMonth) {
          showToast('Please fill in amount and month.', 'warning');
          return;
        }
        const doorId = selectedDoorId || (doors.length === 1 ? doors[0].doorId : null);
        if (!doorId) { showToast('Please select a unit.', 'warning'); return; }
        if (!incomeForm.tenantId) { showToast('Could not resolve tenant for this unit. Please try again.', 'error'); return; }
        const monthOf = incomeForm.forgivenessMonth + '-01'; // "2026-08" → "2026-08-01"
        const parsedAmt = parseFloat(incomeForm.paymentAmount);

        let res;
        if (editingIncomeId && incomeForm.forgiveness_id) {
          const params = new URLSearchParams({
            rent_forgiveness_id: incomeForm.forgiveness_id,
            amount: parsedAmt,
            month_of: monthOf,
            tenant_id: incomeForm.tenantId,
          });
          if (incomeForm.notes) params.append('reason', incomeForm.notes);
          res = await fetch(`${BASE_URL}/EditRentForgiveness?${params}`, { method: 'PUT', headers: { accept: 'application/json' } });
        } else {
          const _now = new Date();
          const _pad = n => String(n).padStart(2, '0');
          const _paydate = `${_now.getFullYear()}-${_pad(_now.getMonth()+1)}-${_pad(_now.getDate())}T${_pad(_now.getHours())}:${_pad(_now.getMinutes())}:${_pad(_now.getSeconds())}`;
          const params = new URLSearchParams({
            door_id: parseInt(doorId, 10),
            tenant_id: incomeForm.tenantId,
            amount: parsedAmt,
            month_of: monthOf,
            paydate: _paydate,
          });
          if (incomeForm.notes) params.append('reason', incomeForm.notes);
          res = await fetch(`${BASE_URL}/AddRentForgiveness?${params}`, { method: 'POST', headers: { accept: 'application/json' } });
        }
        if (res.ok) {
          closeModal();
          await fetchActivities(pmEmailRef.current || userEmail);
          if (!editingIncomeId) window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          const err = await res.text().catch(() => '');
          showToast(`Failed to save rent forgiveness (${res.status}): ${err}`, 'error');
        }
        return;
      }
      // ── End Rent Forgiveness ─────────────────────────────────────────────

      if (!incomeForm.paymentAmount || !incomeForm.date || !incomeForm.method) {
        showToast('Please fill in all required fields', 'warning');
        return;
      }

      if (incomeForm.category === 'Rent' && !incomeForm.month) {
        showToast('Please select a Month Of.', 'warning');
        return;
      }

      if (incomeForm.category === 'Other' && !incomeForm.notes.trim()) {
        showToast('Notes are required when category is Other.', 'warning');
        return;
      }

      const buildParams = (doorId, amount) => {
        const parsedAmount = parseFloat(amount);
        // When editing: preserve the original partial flag from the record.
        // When creating: auto-detect if amount is less than expected rent.
        const isPartial = editingIncomeId
          ? incomeForm.partial
          : (incomeForm.category === 'Rent' && expectedRent !== null && parsedAmount < expectedRent);
        const params = new URLSearchParams({
          door_id: parseInt(doorId, 10),
          amount: parsedAmount,
          paydate: incomeForm.date + 'T' + new Date().toTimeString().slice(0, 8),
          method: incomeForm.category === 'Fee' && (incomeForm.selectedFeeItems || []).length > 0
            ? (incomeForm.selectedFeeItems || []).map(f => f.name).join(', ')
            : incomeForm.method,
          Source: 'App',
          Category: incomeForm.category,
          partial_payment: isPartial ? 1 : 0,
          fees_added: incomeForm.category === 'Fee' ? parsedAmount : '0'
        });
        if (incomeForm.notes) params.append('notes', incomeForm.notes);
        if (incomeForm.category === 'Rent' && incomeForm.month) {
          const yr = incomeForm.date ? new Date(incomeForm.date + 'T00:00:00').getFullYear() : new Date().getFullYear();
          params.append('month_of', `${incomeForm.month} ${yr}`);
        }
        return params;
      };

      // Single door or specific door selected
      if (selectedDoorId || doors.length === 1) {
        const doorId = selectedDoorId || doors[0].doorId;
        const params = buildParams(doorId, incomeForm.paymentAmount);

        let url = `${BASE_URL}/AddRent?${params.toString()}`;
        let method = 'POST';

        if (editingIncomeId) {
          params.append('rent_id', editingIncomeId);
          url = `${BASE_URL}/EditIncome?${params.toString()}`;
          method = 'PUT';
        }

        const res = await fetch(url, { method, headers: { accept: 'application/json' } });
        if (res.ok) {
          showToast('Income added successfully!', 'success');
          closeModal();
          await fetchActivities(pmEmailRef.current || userEmail);
          if (!editingIncomeId) window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          const errorText = await res.text();
          console.error('AddRent failed:', errorText);
          showToast('Failed to add income.', 'error');
        }
        return;
      }

      if (!incomeForm.propertyId) {
        showToast('Please select a property', 'warning');
        return;
      }

      // Multi-family, no door selected — split equally across all doors
      const splitAmount = (parseFloat(incomeForm.paymentAmount) / doors.length).toFixed(2);
      const results = await Promise.allSettled(
        doors.map(door => {
          const params = buildParams(door.doorId, splitAmount);
          return fetch(`${BASE_URL}/AddRent?${params.toString()}`, {
            method: 'POST',
            headers: { accept: 'application/json' }
          });
        })
      );

      const failed = results.filter(r => r.status === 'rejected' || !r.value?.ok).length;
      if (failed > 0) {
        showToast(`Income split: ${doors.length - failed} of ${doors.length} units saved successfully.`, 'success');
      } else {
        showToast(`Income split equally across ${doors.length} units ($${splitAmount} each).`, 'info');
      }
      closeModal();
      await fetchActivities(pmEmailRef.current || userEmail);
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (err) {
      console.error('Error saving income:', err);
      showToast('Error saving income.', 'error');
    }
  };

  // Resolve door → fees → tenant when fee form property/unit changes
  useEffect(() => {
    if (modalType !== 'fee') return;
    const prop = properties.find(p => String(p.id) === String(feeForm.propertyId));
    if (!prop) return;
    const isMulti = parseInt(prop.numUnits) > 1;
    if (isMulti && !feeForm.unitNumber) return;

    const resolve = async () => {
      setFeeFormLoading(true);
      try {
        let doorId = prop.doorId;
        if (isMulti && feeForm.unitNumber) {
          const dr = await fetch(`${BASE_URL}/Door/GetId?email=${encodeURIComponent(pmEmailRef.current)}&street_address=${encodeURIComponent(prop.address)}&unit_number=${encodeURIComponent(feeForm.unitNumber)}`);
          if (dr.ok) { const dd = await dr.json(); doorId = dd.Door_id || dd.door_id || doorId; }
        }
        const [feesRes, tenantRes] = await Promise.all([
          fetch(`${BASE_URL}/GetFeesByDoor?door_id=${doorId}`),
          fetch(`${BASE_URL}/Lease&TenantsByDoor?door_id=${doorId}`),
        ]);
        const fees = feesRes.ok ? ((await feesRes.json()).fees || []) : [];
        let tenantId = null;
        if (tenantRes.ok) {
          const td = await tenantRes.json();
          const tenants = td.tenants || td.Tenants || [];
          tenantId = tenants[0]?.tenant_id || tenants[0]?.id || null;
        }
        setAvailableFees(fees);
        setFeeForm(prev => ({ ...prev, doorId, tenantId, feeId: '', selectedFee: null }));
      } catch (e) { console.error(e); }
      finally { setFeeFormLoading(false); }
    };
    resolve();
  }, [modalType, feeForm.propertyId, feeForm.unitNumber, properties]);

  const handleSaveFee = async () => {
    const { doorId, tenantId, feeId, notes } = feeForm;
    if (!feeId) { showToast('Please select a fee.', 'warning'); return; }
    if (!doorId) { showToast('Please select a property/unit.', 'warning'); return; }
    if (!tenantId) { showToast('Could not resolve tenant. Please try again.', 'error'); return; }
    const _feeNow = new Date();
    const _feePad = n => String(n).padStart(2, '0');
    const _feePaydate = `${_feeNow.getFullYear()}-${_feePad(_feeNow.getMonth()+1)}-${_feePad(_feeNow.getDate())}T${_feePad(_feeNow.getHours())}:${_feePad(_feeNow.getMinutes())}:${_feePad(_feeNow.getSeconds())}`;
    const params = new URLSearchParams({ fee_id: feeId, door_id: doorId, tenant_id: tenantId, paydate: _feePaydate });
    if (notes) params.append('notes', notes);
    try {
      const res = await fetch(`${BASE_URL}/AddTenantFee?${params.toString()}`, { method: 'POST' });
      if (res.ok) {
        closeModal();
        await fetchActivities(pmEmailRef.current || userEmail);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else { showToast('Failed to push fee. Please try again.', 'error'); }
    } catch { showToast('Error pushing fee.', 'error'); }
  };

  const closeModal = () => {
    setModalType(null);
    setEditingIncomeId(null);
    setEditingExpenseId(null);
    const _today = new Date().toISOString().split('T')[0];
    setExpenseForm({ propertyId: '', doorId: '', title: '', amount: '', payDate: _today, category: '', utilityType: '', notes: '' });
    setIncomeForm({ propertyId: '', doorId: '', category: 'Rent', month: '', paymentAmount: '', date: _today, method: '', notes: '', partial: false, forgivenessMonth: '', forgiveness_id: null, tenantId: null, selectedFeeItems: [] });
    setFeeForm({ propertyId: '', unitNumber: '', doorId: null, tenantId: null, feeId: '', notes: '', selectedFee: null });
    setAvailableFees([]);
    setIncomeFeeOptions([]);
    setExpectedRent(null);
    setForgivenessResolveError(false);
    setUnitIsVacant(false);
    setUnitOccupancyChecked(false);
  };

  const openModal = (type) => {
    setModalType(type);
    setShowAddMenu(false);
  };

  const handleEditActivity = (activity) => {
    const prop = properties.find(p => p.name === activity.propertyName);
    setCurrentEditActivity(activity);
    // Keep selectedActivity so Cancel returns user to the detail popup

    if (activity.activityType === 'expense') {
      setEditingExpenseId(activity.id?.replace('expense-', ''));
      setExpenseForm({
        propertyId: prop?.id || '',
        doorId: prop?.doorId || '',
        title: activity.vendor || '',
        amount: Math.abs(activity.amount) || '',
        payDate: activity.rawDate ? activity.rawDate.substring(0, 10) : '',
        category: activity.type || '',
        notes: activity.notes || ''
      });
      openModal('expense');
    } else {
      setEditingIncomeId(activity.id?.replace('rent-', ''));
      const isForgiveness = activity.activityType === 'rent_forgiveness';
      // rawMonthOf is "2026-08-01" → strip to "2026-08" for month input
      const forgivenessMonth = isForgiveness && activity.rawMonthOf
        ? activity.rawMonthOf.substring(0, 7)
        : (isForgiveness && activity.rawDate ? activity.rawDate.substring(0, 7) : '');
      // monthOf is "September 2026" — select only uses the month name
      const monthName = activity.monthOf ? activity.monthOf.split(' ')[0] : '';
      setIncomeForm({
        propertyId: prop?.id || '',
        doorId: prop?.doorId || '',
        category: activity.category || 'Rent',
        month: monthName,
        paymentAmount: Math.abs(activity.amount) || '',
        date: activity.rawDate ? activity.rawDate.substring(0, 10) : '',
        // For fee income, keep raw method (fee names) so we can re-check the right boxes
        method: isForgiveness ? '' : (activity.method || ''),
        notes: activity.notes || '',
        partial: !!(activity.isPartial),
        forgivenessMonth,
        forgiveness_id: activity.forgiveness_id || null,
        tenantId: null,
        selectedFeeItems: [],
      });
      openModal('income');
    }
  };

  const handleDeleteActivity = async (activity) => {
    const isForgiveness = activity.activityType === 'rent_forgiveness';
    const isRent = activity.activityType === 'rent';
    const isPushedFee = activity.activityType === 'pushed_fee';
    const confirmMsg = isForgiveness
      ? `Delete Rent Forgiveness of $${Math.abs(activity.amount).toFixed(2)}?`
      : isPushedFee
        ? `Delete Pushed Fee of $${Math.abs(activity.amount).toFixed(2)}?`
        : isRent
          ? `Delete this income of $${Math.abs(activity.amount).toFixed(2)}?`
          : `Delete this expense of $${Math.abs(activity.amount).toFixed(2)}?`;

    const isIncome = isForgiveness || isRent;
    askConfirm(
      {
        title: isIncome ? 'Delete Income' : 'Delete Expense',
        message: confirmMsg,
        confirmLabel: 'Delete',
        variant: 'danger',
      },
      async () => { try {
      let url;
      if (isForgiveness) {
        url = `${BASE_URL}/DeleteRentForgiveness?rent_forgiveness_id=${activity.forgiveness_id}`;
      } else if (isPushedFee) {
        url = `${BASE_URL}/DeletePushedFee?pushed_fee_id=${activity.pushedFeeId}`;
      } else if (isRent) {
        const id = activity.id?.replace('rent-', '');
        url = `${BASE_URL}/DeleteRent?rent_id=${id}`;
      } else {
        const id = activity.id?.replace('expense-', '');
        url = `${BASE_URL}/DeleteExpense?expense_id=${id}`;
      }

      const res = await fetch(url, { method: 'DELETE', headers: { accept: 'application/json' } });
      if (!res.ok) {
        const err = await res.text();
        console.error('Delete failed:', err);
        showToast('Failed to delete. Please try again.', 'error');
        return;
      }
      setActivities(prev => prev.filter(a => a.id !== activity.id));
      setSelectedActivity(null);
    } catch (err) {
      console.error('Delete error:', err);
      showToast('Error deleting. Please try again.', 'error');
    } }
    );
  };

  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const uniqueProperties = Array.from(new Map(properties.map(p => [p.id, p])).values());

  const getDoorsForProperty = (propertyId) => {
    if (!propertyId) return [];
    // Multi-family: use pre-loaded doors from GetPropertyDetails
    const mapped = doorsMap[String(propertyId)];
    if (mapped && mapped.length > 0) return mapped;
    // Single-family fallback: use the single door from properties
    const prop = properties.find(p => String(p.id) === String(propertyId));
    return prop ? [{ doorId: prop.doorId, unitNumber: prop.unitNumber || '' }] : [];
  };

  const resetToYTD = () => {
    const mostRecentYear = availableYearsFromApi[0] ?? null;
    const now = new Date();
    setFilterYear(mostRecentYear);
    setFilterMonthStart(1);
    setFilterMonthEnd(mostRecentYear === now.getFullYear() ? now.getMonth() + 1 : 12);
    setFilterRent(true);
    setFilterFeeIncome(true);
    setFilterForgiveness(true);
    setFilterPushedFee(true);
    setFilterExpense(true);
    setFilterAmountFrom('');
    setFilterAmountTo('');
    setFilterExpenseCategory('');
    setShowExpenseCats(false);
  };

  // Years derived from both API response + local activity data
  const derivedYears = [...new Set(
    activities.map(a => a.rawDate ? new Date(a.rawDate).getFullYear() : null).filter(Boolean)
  )];
  const availableYears = [...new Set([...availableYearsFromApi, ...derivedYears])].sort((a, b) => b - a);

  const shortMonths = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const filteredActivities = activities.filter((activity) => {
    const t = activity.activityType;
    const isFeeIncome = t === 'rent' && activity.category === 'Fee';
    if ((t === 'expense' || t === 'splitExpense') && !filterExpense) return false;
    if (t === 'rent_forgiveness' && !filterForgiveness) return false;
    if (t === 'pushed_fee'       && !filterPushedFee)  return false;
    if (isFeeIncome              && !filterFeeIncome)  return false;
    if (t === 'rent' && !isFeeIncome && !filterRent)   return false;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!activity.propertyName?.toLowerCase().includes(query) &&
          !activity.vendor?.toLowerCase().includes(query) &&
          !activity.type?.toLowerCase().includes(query)) return false;
    }

    if (filterYear !== null && activity.rawDate) {
      const d = new Date(activity.rawDate);
      if (!isNaN(d.getTime())) {
        if (d.getFullYear() !== filterYear) return false;
        const m = d.getMonth() + 1;
        if (m < filterMonthStart || m > filterMonthEnd) return false;
      }
    }

    const abs = Math.abs(activity.amount);
    if (filterAmountFrom !== '' && abs < parseFloat(filterAmountFrom)) return false;
    if (filterAmountTo !== '' && abs > parseFloat(filterAmountTo)) return false;

    if (filterExpenseCategory && (activity.activityType === 'expense' || activity.activityType === 'splitExpense')) {
      if ((activity.type || '').toLowerCase() !== filterExpenseCategory.toLowerCase()) return false;
    }
    if (filterExpenseCategory && activity.activityType !== 'expense' && activity.activityType !== 'splitExpense') return false;

    return true;
  });

  const totalPages = Math.ceil(filteredActivities.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentActivities = filteredActivities.slice(startIndex, startIndex + itemsPerPage);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  // Stat cards reflect the active filter — forgiveness excluded from income totals
  const totalIncome   = filteredActivities.filter(a => a.amount >= 0 && a.activityType !== 'rent_forgiveness' && (a.activityType !== 'pushed_fee' || a.status === 'paid')).reduce((s, a) => s + a.amount, 0);
  const totalForgiven = filteredActivities.filter(a => a.activityType === 'rent_forgiveness').reduce((s, a) => s + a.amount, 0);
  const totalExpenses = filteredActivities.filter(a => a.amount < 0).reduce((s, a) => s + Math.abs(a.amount), 0);
  const profitMargin  = totalIncome > 0 ? Math.round((totalExpenses / totalIncome) * 100) : 0;
  // Count distinct months that actually have data in the filtered set
  const filteredMonthSet = new Set(
    filteredActivities
      .filter(a => a.rawDate)
      .map(a => { const d = new Date(a.rawDate); return isNaN(d) ? null : `${d.getFullYear()}-${d.getMonth()}`; })
      .filter(Boolean)
  );
  const numMonths = filteredMonthSet.size || 1;
  const avgMonthlyIncome = Math.round(totalIncome / numMonths);
  const avgMonthlyExpense = Math.round(totalExpenses / numMonths);

  const fmtDate = (raw) => {
    if (!raw) return '—';
    const normalized = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw + 'T00:00:00' : raw;
    const d = new Date(normalized);
    return isNaN(d) ? raw : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const expenseDoors = getDoorsForProperty(expenseForm.propertyId);
  const isExpenseMultiNoUnit = expenseDoors.length > 1 && !expenseForm.doorId;

  const incomeDoors = getDoorsForProperty(incomeForm.propertyId);
  const isIncomeMultiNoUnit = incomeDoors.length > 1 && !incomeForm.doorId;

  return (
    <div className="home-page">
      <Sidebar initials={initials} />

      <div className="main-content">
        <Navbar title="Activity" />

        <div className="fm-container">

          {demoMode && (
            <div className="demo-mode-banner">
              <span className="demo-mode-icon">📊</span>
              Sample data — add real transactions to replace this
            </div>
          )}

          <div className="fm-page-header">
            <div>
              <h1 className="fm-page-title">Activity</h1>
              <p className="fm-page-subtitle">Track all income, expenses and transactions</p>
            </div>

            <div id="activity-add-btn" className="fm-header-actions">
              <button className="fm-inline-add-btn fm-inline-add-expense" onClick={() => openModal('expense')}>
                <FaArrowDown /> Add Expense
              </button>
              <button className="fm-inline-add-btn fm-inline-add-income" onClick={() => openModal('income')}>
                <FaArrowUp /> Add Income
              </button>
              <button className="fm-inline-add-btn fm-inline-add-fee" onClick={() => openModal('fee')}>
                <FaBolt /> Push Fee
              </button>
              <button className="fm-inline-add-btn fm-inline-import-btn" onClick={() => navigate('/bank-connection')}>
                <FaUniversity /> Import Transactions
              </button>
              <button
                className={`fm-filter-btn ${showFilter ? 'fm-filter-btn-active' : ''}`}
                onClick={() => setShowFilter(v => !v)}
              >
                <FaSlidersH /> Filters
              </button>
            </div>
          </div>

          {/* STAT CARDS */}
          <div id="activity-stats" className="fm-stats-grid">
            <div className="fm-stat-card">
              <div className="fm-stat-icon-box fm-icon-purple"><FaDollarSign /></div>
              <div className="fm-stat-text">
                <div className="fm-stat-label">Total Transactions</div>
                <div className="fm-stat-value">{filteredActivities.length}</div>
              </div>
            </div>
            <div className="fm-stat-card">
              <div className="fm-stat-icon-box fm-icon-green"><FaDollarSign /></div>
              <div className="fm-stat-text">
                <div className="fm-stat-label">Total Income</div>
                <div className="fm-stat-value">${totalIncome.toLocaleString()}</div>
              </div>
            </div>
            <div className="fm-stat-card">
              <div className="fm-stat-icon-box fm-icon-red"><FaDollarSign /></div>
              <div className="fm-stat-text">
                <div className="fm-stat-label">Total Expenses</div>
                <div className="fm-stat-value">${totalExpenses.toLocaleString()}</div>
              </div>
            </div>
            <div className="fm-stat-card">
              <div className="fm-stat-icon-box fm-icon-blue"><FaArrowDown /></div>
              <div className="fm-stat-text">
                <div className="fm-stat-label">Expense Ratio</div>
                <div className="fm-stat-value" style={{ color: profitMargin > 60 ? '#dc2626' : profitMargin > 35 ? '#d97706' : '#16a34a' }}>
                  {totalIncome > 0 ? `${profitMargin}%` : '—'}
                </div>
              </div>
            </div>
          </div>

          {/* TABLE CARD */}
          <div id="activity-table" className="fm-table-card">
            <div className="fm-table-card-head">
              <h2 className="fm-table-card-title">Recent Transactions</h2>
              <p className="fm-table-card-subtitle">All financial activity for your properties</p>
            </div>

            {loading ? (
              <div className="loading-message">Loading activities...</div>
            ) : (
              <>
                <div className="fm-table-wrap">
                  <table className="fm-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>TITLE</th>
                        <th className="fm-th-amount">AMOUNT</th>
                        <th>TYPE</th>
                        <th>CATEGORY</th>
                        <th>PROPERTY</th>
                        <th>DATE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredActivities.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="no-data-cell">No transactions found.</td>
                        </tr>
                      ) : (
                        currentActivities.flatMap((activity, index) => {
                          if (activity.activityType === 'splitExpense') {
                            const isExpanded = expandedSplits.has(activity.id);
                            const rows = [
                              <tr
                                key={activity.id}
                                className="fm-table-row-clickable fm-split-parent-row"
                                onClick={() => setSelectedActivity(activity)}
                              >
                                <td className="fm-id-col">#{startIndex + index + 1}</td>
                                <td className="fm-title-col">
                                  <span className="fm-split-badge">×{activity.unitCount}</span>
                                  <span className="fm-split-title-text">{activity.vendor}</span>
                                  <span className="fm-split-sub">{activity.propertyName} · Split across {activity.unitCount} units</span>
                                </td>
                                <td><span className="fm-neg">-${Math.abs(activity.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></td>
                                <td><span className="fm-badge fm-badge-red">Expense</span></td>
                                <td>{activity.type}</td>
                                <td><span className="fm-prop-link">{activity.propertyName}</span></td>
                                <td className="fm-split-date-cell">
                                  {activity.date || ''}
                                  <button
                                    className={`fm-split-expand-btn ${isExpanded ? 'fm-split-expand-open' : ''}`}
                                    onClick={e => { e.stopPropagation(); setExpandedSplits(prev => { const n = new Set(prev); n.has(activity.id) ? n.delete(activity.id) : n.add(activity.id); return n; }); }}
                                    title={isExpanded ? 'Collapse' : 'Expand units'}
                                  >
                                    ›
                                  </button>
                                </td>
                              </tr>
                            ];
                            if (isExpanded) {
                              activity.children.forEach(child => {
                                rows.push(
                                  <tr
                                    key={child.id}
                                    className="fm-table-row-clickable fm-split-child-row"
                                    onClick={() => setSelectedActivity(child)}
                                  >
                                    <td />
                                    <td className="fm-title-col" style={{ paddingLeft: 32 }}>{child.unitNumber || child.vendor}</td>
                                    <td><span className="fm-neg">-${Math.abs(child.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></td>
                                    <td />
                                    <td />
                                    <td />
                                    <td>›</td>
                                  </tr>
                                );
                              });
                            }
                            return rows;
                          }

                          return [(
                            <tr
                              key={activity.id || index}
                              className="fm-table-row-clickable"
                              onClick={() => setSelectedActivity(activity)}
                            >
                              <td className="fm-id-col">#{startIndex + index + 1}</td>
                              <td className="fm-title-col">
                                {(() => {
                                  const t = activity.activityType;
                                  if (t === 'expense') return activity.vendor || '';
                                  if (t === 'rent_forgiveness') return 'Rent Forgiveness';
                                  if (t === 'pushed_fee') return activity.type || '';
                                  if (t === 'rent') {
                                    if (activity.category === 'Fee') return activity.method || 'Fee';
                                    if (activity.category === 'Other') return 'Income';
                                    return activity.category || 'Rent';
                                  }
                                  return '';
                                })()}
                              </td>
                              <td>
                                <span className={
                                  activity.activityType === 'rent_forgiveness' ? 'fm-forgiv' :
                                  activity.activityType === 'pushed_fee' ? 'fm-pushed-fee' :
                                  activity.amount >= 0 ? 'fm-pos' : 'fm-neg'
                                }>
                                  {activity.activityType === 'rent_forgiveness'
                                    ? `$${Math.abs(activity.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    : activity.amount < 0
                                    ? `-$${Math.abs(activity.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    : `+$${Math.abs(activity.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                  }
                                </span>
                              </td>
                              <td>
                                {(() => {
                                  const t = activity.activityType;
                                  const isFeeIncome = t === 'rent' && activity.category === 'Fee';
                                  const isOtherIncome = t === 'rent' && activity.category === 'Other';
                                  const cls = t === 'rent_forgiveness' ? 'fm-badge-yellow'
                                    : t === 'pushed_fee' ? 'fm-badge-blue'
                                    : isFeeIncome ? 'fm-badge-purple'
                                    : t === 'rent' ? 'fm-badge-green' : 'fm-badge-red';
                                  const lbl = t === 'rent_forgiveness' ? 'Forgiveness'
                                    : t === 'pushed_fee' ? (activity.status === 'paid' ? 'Paid' : 'Pending')
                                    : isFeeIncome ? 'Fee'
                                    : isOtherIncome ? 'Income'
                                    : t === 'rent' ? 'Rent' : 'Expense';
                                  return <span className={`fm-badge ${cls}`}>{lbl}</span>;
                                })()}
                              </td>
                              <td>
                                {activity.activityType === 'rent' || activity.activityType === 'rent_forgiveness' ? '' : activity.type}
                              </td>
                              <td><span className="fm-prop-link">{activity.propertyName}{(() => { const prop = properties.find(p => p.name === activity.propertyName); return prop && parseInt(prop.numUnits || 0) > 1 && activity.unitNumber ? ` / ${activity.unitNumber}` : ''; })()}</span></td>
                              <td>{activity.date || ''}</td>
                            </tr>
                          )];
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {filteredActivities.length > itemsPerPage && (
                  <div className="pagination-controls">
                    <button className="pagination-btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
                    <div className="pagination-pages">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button key={page} className={`pagination-page ${currentPage === page ? 'active' : ''}`} onClick={() => goToPage(page)}>{page}</button>
                      ))}
                    </div>
                    <button className="pagination-btn" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
                    <span className="pagination-info">Page {currentPage} of {totalPages} ({filteredActivities.length} total)</span>
                  </div>
                )}
              </>
            )}
          </div>

        </div>

        {/* Filter Drawer */}
        {showFilter && (
          <div className="filter-drawer-backdrop" onClick={() => setShowFilter(false)} />
        )}
        <div id="activity-filter-drawer" className={`filter-drawer ${showFilter ? 'filter-drawer-open' : ''}`}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div className="filter-header">
              <h3>Filters</h3>
              <button className="modal-close-btn" onClick={() => setShowFilter(false)}><FaTimes /></button>
            </div>

            <div className="filter-body">
              <div className="filter-section">
                <div className="filter-section-title" style={{ marginBottom: 10 }}>Transaction Type</div>
                <div className="filter-toggle-row">
                  <span className="filter-toggle-label">
                    <span className="flt-type-dot flt-dot-green" />Rent
                  </span>
                  <span className={`flt-switch ${filterRent ? 'flt-switch-on' : ''}`} onClick={() => setFilterRent(v => !v)} />
                </div>
                <div className="filter-toggle-row">
                  <span className="filter-toggle-label">
                    <span className="flt-type-dot flt-dot-purple" />Fee Income
                  </span>
                  <span className={`flt-switch ${filterFeeIncome ? 'flt-switch-on' : ''}`} onClick={() => setFilterFeeIncome(v => !v)} />
                </div>
                <div className="filter-toggle-row">
                  <span className="filter-toggle-label">
                    <span className="flt-type-dot flt-dot-yellow" />Forgiveness
                  </span>
                  <span className={`flt-switch ${filterForgiveness ? 'flt-switch-on' : ''}`} onClick={() => setFilterForgiveness(v => !v)} />
                </div>
                <div className="filter-toggle-row">
                  <span className="filter-toggle-label">
                    <span className="flt-type-dot flt-dot-blue" />Pushed Fees
                  </span>
                  <span className={`flt-switch ${filterPushedFee ? 'flt-switch-on' : ''}`} onClick={() => setFilterPushedFee(v => !v)} />
                </div>
                <div className="filter-toggle-row" style={{ borderBottom: 'none' }}>
                  <span className="filter-toggle-label">
                    <span className="flt-type-dot flt-dot-red" />Expense
                  </span>
                  <span className={`flt-switch ${filterExpense ? 'flt-switch-on' : ''}`} onClick={() => {
                    setFilterExpense(v => {
                      if (v) setFilterExpenseCategory('');
                      return !v;
                    });
                  }} />
                </div>
              </div>

              <div className="filter-section">
                <button className="filter-cat-header" onClick={() => setShowExpenseCats(v => !v)}>
                  <span className="filter-cat-title">Expense Category</span>
                  <span className="filter-cat-right">
                    <span className="filter-cat-val">{filterExpenseCategory || 'All'}</span>
                    <span className={`filter-cat-arrow ${showExpenseCats ? 'filter-cat-arrow-open' : ''}`}>
                      <FaChevronDown />
                    </span>
                  </span>
                </button>
                {showExpenseCats && (
                  <div className="filter-cat-list">
                    {['Repair','Maintenance','Renovation','HOA','Tax','Insurance','Mortgage','Water','Gas','Electricity','Trash','Sewer','Utility','Operation Expense','Other'].map(cat => (
                      <label key={cat} className="filter-radio-row">
                        <span>{cat}</span>
                        <input
                          type="radio"
                          name="expenseCat"
                          checked={filterExpenseCategory === cat}
                          onChange={() => {
                            setFilterExpenseCategory(cat);
                            setFilterExpense(true);
                          }}
                          className="filter-radio"
                        />
                      </label>
                    ))}
                    {filterExpenseCategory && (
                      <button className="filter-clear-link" onClick={() => setFilterExpenseCategory('')}>Clear Category</button>
                    )}
                  </div>
                )}
              </div>

              <div className="filter-section">
                <div className="filter-section-header">
                  <span className="filter-section-title">Date Range</span>
                </div>
                <div className="filter-year-row">
                  {availableYears.map(y => (
                    <button
                      key={y}
                      className={`filter-year-btn ${filterYear === y ? 'filter-year-active' : ''}`}
                      onClick={() => setFilterYear(y)}
                    >{y}</button>
                  ))}
                </div>
                <div className="filter-month-label">
                  {shortMonths[filterMonthStart - 1]} – {shortMonths[filterMonthEnd - 1]} {filterYear}
                </div>
                <div className="dual-range-wrap">
                  <div className="dual-range-track">
                    <div
                      className="dual-range-fill"
                      style={{
                        left: `${((filterMonthStart - 1) / 11) * 100}%`,
                        width: `${((filterMonthEnd - filterMonthStart) / 11) * 100}%`
                      }}
                    />
                  </div>
                  <input
                    type="range" min="1" max="12"
                    value={filterMonthStart}
                    onChange={e => { const v = parseInt(e.target.value); setFilterMonthStart(Math.min(v, filterMonthEnd)); }}
                  />
                  <input
                    type="range" min="1" max="12"
                    value={filterMonthEnd}
                    onChange={e => { const v = parseInt(e.target.value); setFilterMonthEnd(Math.max(v, filterMonthStart)); }}
                  />
                </div>
                <div className="filter-month-ends"><span>Jan</span><span>Dec</span></div>
              </div>

              <div className="filter-section">
                <div className="filter-section-title" style={{ marginBottom: 14 }}>Amount</div>
                <div className="filter-amount-row">
                  <div className="filter-amount-field">
                    <label>From</label>
                    <input type="number" value={filterAmountFrom} onChange={e => setFilterAmountFrom(e.target.value)} placeholder="0" />
                  </div>
                  <div className="filter-amount-field">
                    <label>To</label>
                    <input type="number" value={filterAmountTo} onChange={e => setFilterAmountTo(e.target.value)} placeholder="0" />
                  </div>
                </div>
                {(filterAmountFrom !== '' || filterAmountTo !== '') && (
                  <button className="filter-clear-link" onClick={() => { setFilterAmountFrom(''); setFilterAmountTo(''); }}>Clear Amount</button>
                )}
              </div>
            </div>

            <div className="filter-footer">
              <button className="filter-reset-btn" onClick={resetToYTD}>Reset to YTD</button>
              <p className="filter-note">Default view shows year-to-date activity. Totals update as you filter.</p>
            </div>
          </div>
        </div>

        {/* Split Expense Detail Modal */}
        {selectedActivity?.activityType === 'splitExpense' && (
          <div className="rd-modal-overlay" onClick={() => setSelectedActivity(null)}>
            <div className="rd-modal" onClick={e => e.stopPropagation()}>
              <button className="rd-modal-close" onClick={() => setSelectedActivity(null)}><FaTimes /></button>
              <h2 className="rd-modal-title">Expense Details</h2>
              <div className="rd-modal-hero rd-modal-hero-split">
                <div className="rd-modal-hero-label">{selectedActivity.type || 'Expense'}</div>
                <div className="rd-mhv-split rd-modal-hero-val">
                  −${Math.abs(selectedActivity.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="rd-modal-hero-sub">{selectedActivity.vendor}</div>
              </div>
              <div className="rd-modal-section-label">Details</div>
              <div className="rd-modal-rows">
                <div className="rd-mrow">
                  <span className="rd-mrow-icon rd-mri-blue"><FaBuilding /></span>
                  <span className="rd-mrow-label">Property</span>
                  <span className="rd-mrow-val">{selectedActivity.propertyName || '—'}</span>
                </div>
                <div className="rd-mrow">
                  <span className="rd-mrow-icon rd-mri-orange"><FaUser /></span>
                  <span className="rd-mrow-label">Units</span>
                  <span className="rd-mrow-val">{selectedActivity.unitCount} units</span>
                </div>
                <div className="rd-mrow">
                  <span className="rd-mrow-icon rd-mri-orange"><FaCalendarAlt /></span>
                  <span className="rd-mrow-label">Date</span>
                  <span className="rd-mrow-val">{fmtDate(selectedActivity.rawDate)}</span>
                </div>
                <div className="rd-mrow">
                  <span className="rd-mrow-icon rd-mri-blue"><FaDollarSign /></span>
                  <span className="rd-mrow-label">Per unit</span>
                  <span className="rd-mrow-val">${selectedActivity.perUnit?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="rd-mrow">
                  <span className="rd-mrow-icon rd-mri-blue"><FaTag /></span>
                  <span className="rd-mrow-label">Source</span>
                  <span className="rd-mrow-val">App</span>
                </div>
              </div>
              <div className="rd-modal-section-label" style={{ marginTop: 16 }}>Breakdown</div>
              <div className="rd-split-breakdown">
                {selectedActivity.children.map(child => (
                  <div key={child.id} className="rd-split-breakdown-row" onClick={() => setSelectedActivity(child)}>
                    <span className="rd-split-unit-name">{child.unitNumber || child.vendor}</span>
                    <span className="rd-split-unit-amt fm-neg">
                      −${Math.abs(child.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <span className="rd-split-unit-arrow">›</span>
                  </div>
                ))}
              </div>
              {selectedActivity.notes ? (
                <>
                  <div className="rd-modal-section-label" style={{ marginTop: 16 }}>Notes</div>
                  <div className="rd-modal-notes">{selectedActivity.notes}</div>
                </>
              ) : null}
            </div>
          </div>
        )}

        {/* Transaction Detail Modal */}
        {selectedActivity && selectedActivity.activityType !== 'splitExpense' && (
          <div className="rd-modal-overlay" onClick={() => setSelectedActivity(null)}>
            <div className="rd-modal" onClick={e => e.stopPropagation()}>
              <button className="rd-modal-close" onClick={() => setSelectedActivity(null)}><FaTimes /></button>

              {(() => {
                const isFG = selectedActivity.activityType === 'rent_forgiveness';
                const isPF = selectedActivity.activityType === 'pushed_fee';
                const isRent = selectedActivity.activityType === 'rent';
                const isFeeIncome = isRent && selectedActivity.category === 'Fee';
                const modalTitle = isFG ? 'Forgiveness Details' : isPF ? 'Fee Details' : isFeeIncome ? 'Fee Details' : isRent ? 'Income Details' : 'Expense Details';
                return <h2 className="rd-modal-title">{modalTitle}</h2>;
              })()}

              {/* Hero amount */}
              {(() => {
                const isFG = selectedActivity.activityType === 'rent_forgiveness';
                const isPF = selectedActivity.activityType === 'pushed_fee';
                const isRent = selectedActivity.activityType === 'rent';
                const isFeeIncome = isRent && selectedActivity.category === 'Fee';
                const heroClass = isFG ? 'rd-modal-hero-yellow' : isPF ? 'rd-modal-hero-blue' : isRent ? 'rd-modal-hero-green' : 'rd-modal-hero-red';
                const valClass = isFG ? 'rd-mhv-yellow' : isPF ? 'rd-mhv-blue' : isRent ? 'rd-mhv-green' : 'rd-mhv-red';
                const label = isFG ? 'Rent Forgiveness' : isPF ? (selectedActivity.type || 'Pushed Fee') : isFeeIncome ? 'Fee' : (selectedActivity.type || (isRent ? 'Rent' : 'Expense'));
                const prefix = isFG ? '' : '+';
                const heroSub = selectedActivity.vendor || fmtDate(selectedActivity.rawDate);
                return (
                  <div className={`rd-modal-hero ${heroClass}`}>
                    <div className="rd-modal-hero-label">{label}</div>
                    {isPF && selectedActivity.status && (
                      <span className={`rd-hero-fee-status ${selectedActivity.status === 'paid' ? 'rd-fee-paid' : 'rd-fee-pending'}`}>
                        {selectedActivity.status === 'paid' ? '✓ Paid' : '⏳ Pending'}
                      </span>
                    )}
                    <div className={`rd-modal-hero-val ${valClass}`}>
                      {selectedActivity.amount < 0 ? '−' : prefix}${Math.abs(selectedActivity.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    {heroSub && <div className="rd-modal-hero-sub">{heroSub}</div>}
                    {selectedActivity.isPartial && (
                      <div className="rd-hero-badges">
                        <span className="rd-hero-badge rd-hero-badge-partial">Partial Payment</span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Details */}
              <div className="rd-modal-section-label">Details</div>
              <div className="rd-modal-rows">
                <div className="rd-mrow">
                  <span className="rd-mrow-icon rd-mri-blue"><FaBuilding /></span>
                  <span className="rd-mrow-label">Property</span>
                  <span className="rd-mrow-val">
                    {selectedActivity.propertyName || '—'}
                    {(() => {
                      if (!selectedActivity.unitNumber) return '';
                      const propMatch = properties.find(p => p.name === selectedActivity.propertyName);
                      const isMultiUnit = propMatch ? parseInt(propMatch.numUnits || 0) > 1 : false;
                      return isMultiUnit ? ` / ${selectedActivity.unitNumber}` : '';
                    })()}
                  </span>
                </div>
                <div className="rd-mrow">
                  <span className="rd-mrow-icon rd-mri-orange"><FaCalendarAlt /></span>
                  <span className="rd-mrow-label">Date</span>
                  <span className="rd-mrow-val">{fmtDate(selectedActivity.rawDate)}</span>
                </div>
                {selectedActivity.monthOf && <>
                  <div className="rd-modal-divider" />
                  <div className="rd-mrow">
                    <span className="rd-mrow-icon rd-mri-orange"><FaCalendarAlt /></span>
                    <span className="rd-mrow-label">Month Of</span>
                    <span className="rd-mrow-val">{selectedActivity.monthOf}</span>
                  </div>
                </>}
                {selectedActivity.notes && <>
                  <div className="rd-modal-divider" />
                  <div className="rd-mrow">
                    <span className="rd-mrow-icon rd-mri-gray"><FaStickyNote /></span>
                    <span className="rd-mrow-label">Notes</span>
                    <span className="rd-mrow-val">{selectedActivity.notes}</span>
                  </div>
                </>}
              </div>

              {/* Actions */}
              <div className="rd-modal-divider" style={{ marginTop: 18 }} />
              <div className="txn-modal-actions" style={{ justifyContent: 'flex-end' }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="txn-modal-close-btn" onClick={() => setSelectedActivity(null)}>Close</button>
                  {selectedActivity.activityType === 'pushed_fee' && selectedActivity.status !== 'paid' && (
                    <button className="txn-modal-markpaid-btn" onClick={async () => {
                      const res = await fetch(`${BASE_URL}/UpdatePushedFeeStatus?pushed_fee_id=${selectedActivity.pushedFeeId}&status=paid`, { method: 'PUT' });
                      if (res.ok) {
                        setActivities(prev => prev.map(a => a.id === selectedActivity.id ? { ...a, status: 'paid' } : a));
                        setSelectedActivity(prev => ({ ...prev, status: 'paid' }));
                      }
                    }}>
                      <FaCheckCircle /> Mark Paid
                    </button>
                  )}
                  {selectedActivity.activityType !== 'pushed_fee' && (
                    <button className="txn-modal-edit-btn" onClick={() => handleEditActivity(selectedActivity)}>
                      <FaEdit /> Edit
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Expense Modal */}
        {modalType === 'expense' && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="new-activity-modal modal-expense" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{editingExpenseId ? 'Edit Expense' : 'Add Expense'}</h3>
                <button className="modal-close-btn" onClick={closeModal}><FaTimes /></button>
              </div>

              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Property *</label>
                    <select name="propertyId" value={expenseForm.propertyId} onChange={handleExpenseChange}>
                      <option value="">Select Property</option>
                      {uniqueProperties.map(prop => (
                        <option key={prop.id} value={prop.id}>{prop.name} - {prop.address}</option>
                      ))}
                    </select>
                    {expenseForm.propertyId && (() => {
                      const prop = properties.find(p => String(p.id) === String(expenseForm.propertyId));
                      const s = prop?.status;
                      if (!s) return null;
                      const isOcc = s === 'Occupied';
                      return <div style={{ marginTop: 6, fontWeight: 700, fontSize: 12, color: isOcc ? '#16a34a' : '#dc2626' }}>{s}</div>;
                    })()}
                  </div>
                </div>

                {expenseDoors.length > 1 && (
                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Unit <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 12 }}>(leave blank to split equally)</span></label>
                      <select name="doorId" value={expenseForm.doorId} onChange={handleExpenseChange}>
                        <option value="">Split across all units</option>
                        {expenseDoors.map(door => (
                          <option key={door.doorId} value={door.doorId}>Unit {door.unitNumber || door.doorId}</option>
                        ))}
                      </select>
                      {isExpenseMultiNoUnit && expenseForm.amount && (
                        <div className="split-hint">
                          Each unit will be charged ${(parseFloat(expenseForm.amount || 0) / expenseDoors.length).toFixed(2)} ({expenseDoors.length} units)
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>Title *</label>
                    <input type="text" name="title" value={expenseForm.title} onChange={handleExpenseChange} placeholder="Expense title" />
                  </div>
                  <div className="form-group">
                    <label>Amount *</label>
                    <input type="number" name="amount" value={expenseForm.amount} onChange={handleExpenseChange} placeholder="0.00" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Pay Date *</label>
                    <input type="date" name="payDate" value={expenseForm.payDate} onChange={handleExpenseChange} />
                  </div>
                  <div className="form-group">
                    <label>Category *</label>
                    <select name="category" value={expenseForm.category} onChange={handleExpenseChange}>
                      <option value="">Select Category</option>
                      <option value="Repair">Repair</option>
                      <option value="Operation Expense">Operation Expense</option>
                      <option value="Utility">Utility</option>
                      <option value="Maintenance">Maintenance</option>
                      <option value="Renovation">Renovation</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                {expenseForm.category === 'Operation Expense' && (
                  <div className="utility-type-box">
                    <div className="form-group">
                      <label>Type *</label>
                      <select name="utilityType" value={expenseForm.utilityType} onChange={handleExpenseChange}>
                        <option value="">Select Type</option>
                        <option value="General">General</option>
                        <option value="HOA">HOA</option>
                        <option value="Tax">Tax</option>
                        <option value="Insurance">Insurance</option>
                        <option value="Mortgage">Mortgage</option>
                      </select>
                    </div>
                  </div>
                )}

                {expenseForm.category === 'Utility' && (
                  <div className="utility-type-box">
                    <div className="form-group">
                      <label>Type *</label>
                      <select name="utilityType" value={expenseForm.utilityType} onChange={handleExpenseChange}>
                        <option value="">Select Type</option>
                        <option value="General">General</option>
                        <option value="Water">Water</option>
                        <option value="Gas">Gas</option>
                        <option value="Electricity">Electricity</option>
                        <option value="Trash">Trash</option>
                        <option value="Sewer">Sewer</option>
                      </select>
                    </div>
                  </div>
                )}

                <div className="form-row single">
                  <div className="form-group">
                    <label>Notes {expenseForm.category === 'Other' ? '*' : ''}</label>
                    <textarea
                      name="notes"
                      value={expenseForm.notes}
                      onChange={handleExpenseChange}
                      placeholder={expenseForm.category === 'Other' ? 'Notes are required for Other...' : 'Add any additional notes...'}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <div>
                  {editingExpenseId && (
                    <button className="btn-cancel" style={{ color: '#dc2626', borderColor: '#fca5a5' }}
                      onClick={() => { handleDeleteActivity(currentEditActivity); closeModal(); }}>
                      <FaTrash style={{ marginRight: 6 }} /> Delete
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-cancel" onClick={closeModal}>Cancel</button>
                  <button className="btn-save" onClick={handleSaveExpense}>
                    {editingExpenseId ? 'Update Expense' : 'Save Expense'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Income Modal */}
        {modalType === 'income' && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="new-activity-modal modal-income" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{editingIncomeId ? 'Edit Income' : 'Add Income'}</h3>
                <button className="modal-close-btn" onClick={closeModal}><FaTimes /></button>
              </div>

              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Property *</label>
                    <select name="propertyId" value={incomeForm.propertyId} onChange={handleIncomeChange}>
                      <option value="">Select Property</option>
                      {uniqueProperties.map(prop => (
                        <option key={prop.id} value={prop.id}>{prop.name} - {prop.address}</option>
                      ))}
                    </select>
                    {/* Status badge under property selector */}
                    {incomeForm.propertyId && incomeForm.category !== 'Rent Forgiveness' && incomeDoors.length <= 1 && unitOccupancyChecked && (
                      unitIsVacant
                        ? <div style={{ marginTop: 6, fontWeight: 700, fontSize: 12, color: '#dc2626' }}>Vacant</div>
                        : <div style={{ marginTop: 6, fontWeight: 700, fontSize: 12, color: '#16a34a' }}>Occupied</div>
                    )}
                    {/* Single-unit forgiveness: show Occupied / Vacant under property */}
                    {incomeForm.category === 'Rent Forgiveness' && incomeDoors.length <= 1 && incomeForm.propertyId && (
                      forgivenessResolveError
                        ? <div className="act-forgiveness-tenant-note act-forgiveness-tenant-warn" style={{ marginTop: 6, color: '#dc2626', fontWeight: 700 }}>Vacant</div>
                        : incomeForm.tenantId
                        ? <div className="act-forgiveness-tenant-note" style={{ marginTop: 6, color: '#16a34a', fontWeight: 700 }}>Occupied</div>
                        : null
                    )}
                  </div>
                </div>

                {incomeDoors.length > 1 && (
                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Unit <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 12 }}>(leave blank to split equally)</span></label>
                      <select name="doorId" value={incomeForm.doorId} onChange={handleIncomeChange}>
                        <option value="">Split across all units</option>
                        {incomeDoors.map(door => (
                          <option key={door.doorId} value={door.doorId}>Unit {door.unitNumber || door.doorId}</option>
                        ))}
                      </select>
                      {isIncomeMultiNoUnit && incomeForm.paymentAmount && (
                        <div className="split-hint">
                          Each unit will receive ${(parseFloat(incomeForm.paymentAmount || 0) / incomeDoors.length).toFixed(2)} ({incomeDoors.length} units)
                        </div>
                      )}
                      {/* Multi-unit non-forgiveness: show Occupied/Vacant for selected unit */}
                      {incomeForm.category !== 'Rent Forgiveness' && incomeForm.doorId && unitOccupancyChecked && (
                        unitIsVacant
                          ? <div style={{ marginTop: 6, fontWeight: 700, fontSize: 12, color: '#dc2626' }}>Vacant</div>
                          : <div style={{ marginTop: 6, fontWeight: 700, fontSize: 12, color: '#16a34a' }}>Occupied</div>
                      )}
                      {/* Multi-unit forgiveness: show Property / Unit + Occupied/Vacant under unit selector */}
                      {incomeForm.category === 'Rent Forgiveness' && incomeForm.doorId && (() => {
                        const propName = properties.find(p => String(p.id) === String(incomeForm.propertyId))?.name || '';
                        const unitNum  = incomeDoors.find(d => String(d.doorId) === String(incomeForm.doorId))?.unitNumber || incomeForm.doorId;
                        return (
                          <>
                            <div className="act-forgiveness-unit-label" style={{ marginTop: 6 }}>
                              {propName} / Unit {unitNum}
                            </div>
                            {forgivenessResolveError
                              ? <div className="act-forgiveness-tenant-note act-forgiveness-tenant-warn" style={{ marginTop: 4, color: '#dc2626', fontWeight: 700 }}>Vacant</div>
                              : incomeForm.tenantId
                              ? <div className="act-forgiveness-tenant-note" style={{ marginTop: 4, color: '#16a34a', fontWeight: 700 }}>Occupied</div>
                              : null
                            }
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>Category *</label>
                    <select name="category" value={incomeForm.category} onChange={handleIncomeChange}>
                      <option value="Rent">Rent</option>
                      <option value="Fee">Fee</option>
                      <option value="Rent Forgiveness">Rent Forgiveness</option>
                      <option value="Other">Other</option>
                    </select>
                    {editingIncomeId && incomeForm.partial && (
                      <div style={{ fontSize: 12, color: '#92400e', marginTop: 4, background: '#fef3c7', borderRadius: 6, padding: '3px 8px', display: 'inline-block' }}>
                        Partial Payment — status will be preserved
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>
                      {incomeForm.category === 'Rent Forgiveness' ? 'Forgiveness Amount *' : 'Payment Amount *'}
                      {incomeForm.category === 'Rent' && incomeForm.paymentAmount && expectedRent !== null && (
                        parseFloat(incomeForm.paymentAmount) < expectedRent
                          ? <span style={{ fontWeight: 400, color: '#d97706', fontSize: 12, marginLeft: 6 }}>Partial payment</span>
                          : <span style={{ fontWeight: 400, color: '#16a34a', fontSize: 12, marginLeft: 6 }}>auto-filled</span>
                      )}
                    </label>
                    <input
                      type="number"
                      name="paymentAmount"
                      value={incomeForm.paymentAmount}
                      onChange={handleIncomeChange}
                      placeholder="0.00"
                    />
                    {incomeForm.category === 'Rent' && expectedRent !== null &&
                      parseFloat(incomeForm.paymentAmount || 0) < expectedRent && (
                      <div style={{ fontSize: 12, color: '#d97706', marginTop: 4 }}>
                        Rent due: ${expectedRent.toLocaleString('en-US', { minimumFractionDigits: 2 })} — this will be marked as a partial payment
                      </div>
                    )}
                  </div>
                </div>

                {/* Fee type multi-select (checkboxes) */}
                {incomeForm.category === 'Fee' && (
                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>
                        Fee Type *
                        {incomeForm.selectedFeeItems.length > 0 && (
                          <span style={{ fontWeight: 400, fontSize: 12, color: '#16a34a', marginLeft: 8 }}>
                            {incomeForm.selectedFeeItems.length} selected — total ${incomeForm.selectedFeeItems.reduce((s, f) => s + f.amount, 0).toFixed(2)}
                          </span>
                        )}
                      </label>
                      {incomeFeeLoading ? (
                        <div style={{ fontSize: 13, color: '#6b7280', padding: '6px 0' }}>Loading fees...</div>
                      ) : incomeFeeOptions.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#9ca3af', padding: '6px 0' }}>
                          {incomeForm.doorId || (getDoorsForProperty(incomeForm.propertyId).length === 1) ? 'No fees found for this unit.' : 'Select a property/unit first.'}
                        </div>
                      ) : (
                        <div className="income-fee-checklist">
                          {incomeFeeOptions.map((f, i) => {
                            const fid = String(f.id || f.Fee_Id || i);
                            const fname = f.Type || f.type || f.Fee_Type || f.fee_name || f.name || 'Fee';
                            const famt = parseFloat(f.Amount || f.amount || 0);
                            const isChecked = incomeForm.selectedFeeItems.some(item => item.id === fid);
                            return (
                              <label key={fid} className={`income-fee-check-item ${isChecked ? 'income-fee-check-item-on' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => {
                                    setIncomeForm(prev => {
                                      const already = prev.selectedFeeItems.some(item => item.id === fid);
                                      const newItems = already
                                        ? prev.selectedFeeItems.filter(item => item.id !== fid)
                                        : [...prev.selectedFeeItems, { id: fid, name: fname, amount: famt }];
                                      const total = newItems.reduce((sum, item) => sum + item.amount, 0);
                                      return { ...prev, selectedFeeItems: newItems, paymentAmount: newItems.length > 0 ? total.toFixed(2) : '' };
                                    });
                                  }}
                                />
                                <span className="income-fee-check-name">{fname}</span>
                                <span className="income-fee-check-amt">${famt.toFixed(2)}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Rent Forgiveness specific fields */}
                {incomeForm.category === 'Rent Forgiveness' && (
                  <>
                    {incomeDoors.length > 1 && !incomeForm.doorId && incomeForm.paymentAmount && (
                      <div className="form-row">
                        <div className="form-group full-width">
                          <div className="split-hint">
                            Each unit will receive ${(parseFloat(incomeForm.paymentAmount || 0) / incomeDoors.length).toFixed(2)} ({incomeDoors.length} units)
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="form-row">
                      <div className="form-group full-width">
                        <label>Month Of *</label>
                        <input
                          type="month"
                          name="forgivenessMonth"
                          value={incomeForm.forgivenessMonth}
                          onChange={e => setIncomeForm(prev => ({ ...prev, forgivenessMonth: e.target.value }))}
                          className="act-month-input"
                        />
                      </div>
                    </div>
                    <div className="form-row single">
                      <div className="form-group">
                        <label>Reason (optional)</label>
                        <textarea
                          name="notes"
                          value={incomeForm.notes}
                          onChange={handleIncomeChange}
                          placeholder="e.g. Hardship accommodation, agreed reduction..."
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Standard Rent / Fee / Other fields */}
                {incomeForm.category !== 'Rent Forgiveness' && (
                  <>
                    {incomeForm.category === 'Rent' && (
                      <div className="form-row">
                        <div className="form-group full-width">
                          <label>Month Of *</label>
                          <select name="month" value={incomeForm.month} onChange={handleIncomeChange}>
                            <option value="">Select Month</option>
                            {months.map(month => (
                              <option key={month} value={month}>{month}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                    <div className="form-row">
                      <div className="form-group">
                        <label>Date *</label>
                        <input type="date" name="date" value={incomeForm.date} onChange={handleIncomeChange} />
                      </div>
                      <div className="form-group">
                        <label>Payment Method *</label>
                        <select name="method" value={incomeForm.method} onChange={handleIncomeChange}>
                          <option value="">Select Method</option>
                          <option value="Cash">Cash</option>
                          <option value="Check">Check</option>
                          <option value="Bank Transfer">Bank Transfer</option>
                          <option value="Zelle">Zelle</option>
                          <option value="Venmo">Venmo</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-row single">
                      <div className="form-group">
                        <label>Notes {incomeForm.category === 'Other' ? '*' : ''}</label>
                        <textarea
                          name="notes"
                          value={incomeForm.notes}
                          onChange={handleIncomeChange}
                          placeholder={incomeForm.category === 'Other' ? 'Notes are required...' : 'Add any additional notes...'}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <div>
                  {editingIncomeId && (
                    <button className="btn-cancel" style={{ color: '#dc2626', borderColor: '#fca5a5' }}
                      onClick={() => { handleDeleteActivity(currentEditActivity); closeModal(); }}>
                      <FaTrash style={{ marginRight: 6 }} /> Delete
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {incomeForm.category === 'Rent Forgiveness' && forgivenessResolveError && (
                    <div style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 10px' }}>
                      Property is vacant — rent forgiveness is not available.
                    </div>
                  )}
                  <button className="btn-cancel" onClick={closeModal}>Cancel</button>
                  <button
                    className="btn-save"
                    onClick={handleSaveIncome}
                    disabled={incomeForm.category === 'Rent Forgiveness' && forgivenessResolveError}
                    style={incomeForm.category === 'Rent Forgiveness' && forgivenessResolveError ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  >
                    {editingIncomeId ? 'Update Income' : 'Save Income'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Push Fee Modal */}
      {modalType === 'fee' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="new-activity-modal modal-fee" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title"><FaBolt style={{ color: '#2563eb', marginRight: 8 }} />Push Fee to Tenant</h2>
              <button className="modal-close-btn" onClick={closeModal}><FaTimes /></button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Property *</label>
                  <select value={feeForm.propertyId} onChange={e => setFeeForm(prev => ({ ...prev, propertyId: e.target.value, unitNumber: '', doorId: null, tenantId: null, feeId: '', selectedFee: null }))}>
                    <option value="">Select Property</option>
                    {[...new Map(properties.map(p => [p.id, p])).values()].map(p => (
                      <option key={p.id} value={p.id}>{p.name}{p.address ? ` - ${p.address}` : ''}</option>
                    ))}
                  </select>
                  {feeForm.propertyId && (() => {
                    const prop = properties.find(p => String(p.id) === String(feeForm.propertyId));
                    const s = prop?.status;
                    if (!s) return null;
                    const isOcc = s === 'Occupied';
                    return <div style={{ marginTop: 6, fontWeight: 700, fontSize: 12, color: isOcc ? '#16a34a' : '#dc2626' }}>{s}</div>;
                  })()}
                </div>
              </div>

              {feeForm.propertyId && (() => {
                const prop = properties.find(p => String(p.id) === String(feeForm.propertyId));
                const doors = doorsMap[feeForm.propertyId] || [];
                const isMulti = parseInt(prop?.numUnits) > 1 && doors.length > 0;
                if (!isMulti) return null;
                return (
                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Unit *</label>
                      <select value={feeForm.unitNumber} onChange={e => setFeeForm(prev => ({ ...prev, unitNumber: e.target.value, doorId: null, tenantId: null, feeId: '', selectedFee: null }))}>
                        <option value="">Select Unit</option>
                        {doors.map(d => (
                          <option key={d.doorId} value={d.unitNumber}>{d.unitNumber}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              })()}

              {feeFormLoading && <div className="act-forgiveness-tenant-warn" style={{ marginBottom: 8 }}>Loading fees...</div>}

              {!feeFormLoading && availableFees.length > 0 && (
                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Fee *</label>
                    <select value={feeForm.feeId} onChange={e => {
                      const fee = availableFees.find(f => String(f.id || f.Fee_Id) === e.target.value);
                      setFeeForm(prev => ({ ...prev, feeId: e.target.value, selectedFee: fee || null }));
                    }}>
                      <option value="">Select Fee</option>
                      {availableFees.map(f => {
                        const fid = f.id || f.Fee_Id;
                        const ftype = f.Type || f.type || f.Fee_Type || f.fee_name || f.name || 'Fee';
                        const famt = f.Amount || f.amount || 0;
                        return <option key={fid} value={fid}>{ftype} — ${Number(famt).toFixed(2)}</option>;
                      })}
                    </select>
                    {feeForm.selectedFee && (
                      <div className="act-forgiveness-tenant-note" style={{ marginTop: 4 }}>
                        Amount: <strong>${Number(feeForm.selectedFee.Amount || feeForm.selectedFee.amount || 0).toFixed(2)}</strong>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!feeFormLoading && feeForm.doorId && availableFees.length === 0 && (
                <div className="act-forgiveness-tenant-warn" style={{ marginBottom: 8 }}>
                  No fees configured for this unit's lease.
                </div>
              )}

              {feeForm.doorId && (
                feeForm.tenantId
                  ? <div style={{ marginBottom: 8, fontWeight: 700, fontSize: 12, color: '#16a34a' }}>Occupied</div>
                  : !feeFormLoading && <div style={{ marginBottom: 8, fontWeight: 700, fontSize: 12, color: '#dc2626' }}>Vacant</div>
              )}

              <div className="form-row single">
                <div className="form-group">
                  <label>Notes (optional)</label>
                  <textarea
                    value={feeForm.notes}
                    onChange={e => setFeeForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="e.g. Late payment on August rent..."
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>Cancel</button>
              <button
                className="modal-save-btn modal-save-btn-blue"
                onClick={handleSaveFee}
                disabled={!feeForm.feeId || !feeForm.tenantId || feeFormLoading}
              >
                <FaBolt /> Push Fee
              </button>
            </div>
          </div>
        </div>
      )}

      {userEmail && (
        <Tutorial
          steps={[
            { title: 'Welcome to Activity!', description: 'Your financial command center — every rent payment and expense tracked in one scrollable feed.', anchorId: null, cardAlignment: 'bottom' },
            { title: 'Summary Stats', description: 'These four cards show your filtered totals: total transactions, income collected, expenses paid, and expense ratio — all updating as you filter.', anchorId: 'activity-stats' },
            { title: 'Transaction Feed', description: 'Every rent collection and expense lives here. Click any row to view full details, edit amounts, or delete the entry.', anchorId: 'activity-table' },
            { title: 'Filter Your View', description: 'Use this panel to narrow by income vs expense, category, date range, or amount. Everything in the feed and stats updates instantly.', anchorId: 'activity-filter-drawer' },
            { title: 'Add Transaction', description: 'Tap "+ Add Transaction" to record a rent payment or log an expense. You can also import transactions straight from a connected bank account.', anchorId: 'activity-add-btn' },
          ]}
          storageKey={`hasSeenActivityTutorial_${userEmail}`}
          onStep={handleTutorialStep}
        />
      )}
      {confirmDialog && (
        <ConfirmModal
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          variant={confirmDialog.variant || 'danger'}
          onConfirm={() => { confirmDialog.onConfirm(); setConfirmDialog(null); }}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </div>
  );
}

export default Activity;
